/**
 * Resource preloader utility for critical assets
 */

class ResourcePreloader {
  constructor() {
    this.preloadedResources = new Set();
    this.preloadPromises = new Map();
  }

  /**
   * Preload a critical resource
   * @param {string} href - Resource URL
   * @param {string} as - Resource type (image, style, script, etc.)
   * @param {Object} options - Additional options
   */
  preloadResource(href, as, options = {}) {
    if (this.preloadedResources.has(href)) {
      return Promise.resolve();
    }

    if (this.preloadPromises.has(href)) {
      return this.preloadPromises.get(href);
    }

    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = href;
      link.as = as;
      
      if (options.crossOrigin) {
        link.crossOrigin = options.crossOrigin;
      }
      
      if (options.type) {
        link.type = options.type;
      }

      link.onload = () => {
        this.preloadedResources.add(href);
        resolve();
      };

      link.onerror = (error) => {
        // Only log warnings in development to avoid console spam
        if (import.meta.env?.DEV) {
          console.warn(`Failed to preload resource: ${href}`, error);
        }
        // Still resolve to prevent blocking other preloads
        resolve();
      };

      document.head.appendChild(link);
    });

    this.preloadPromises.set(href, promise);
    return promise;
  }

  /**
   * Preload critical fonts
   */
  preloadCriticalFonts() {
    const fonts = [
      {
        href: 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700&family=Montserrat:wght@400;500;700&display=swap',
        as: 'style'
      }
    ];

    return Promise.all(
      fonts.map(font => this.preloadResource(font.href, font.as))
    );
  }

  /**
   * Preload critical images
   */
  preloadCriticalImages() {
    // Skip preloading local images since the app uses Unsplash images
    // const images = [
    //   '/images/optimized/hero-bg.webp',
    //   '/images/optimized/hero-bg.jpg',
    //   '/images/optimized/logo.webp',
    //   '/images/optimized/logo.png'
    // ];

    // return Promise.all(
    //   images.map(image => this.preloadResource(image, 'image').catch(error => {
    //     console.warn(`Failed to preload image ${image}:`, error.message);
    //     return null; // Don't fail the entire preload process
    //   }))
    // );
    return Promise.resolve();
  }

  /**
   * Preload critical scripts
   * Vite already injects hashed modulepreload links in production index.html.
   * Do not preload /src/*.jsx (those paths do not exist after build) or use as="modulepreload".
   */
  preloadCriticalScripts() {
    return Promise.resolve();
  }

  /**
   * Preload all critical resources
   */
  async preloadAll() {
    try {
      await Promise.all([
        this.preloadCriticalFonts(),
        this.preloadCriticalImages(),
        this.preloadCriticalScripts()
      ]);
      
      if (import.meta.env.DEV) {
        console.log('Critical resources preloaded successfully');
      }
    } catch (error) {
      console.warn('Some resources failed to preload:', error);
    }
  }
}

export default new ResourcePreloader();
